import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner } from '@astryxdesign/core/Banner';
import { Button } from '@astryxdesign/core/Button';
import { StatusLine } from '@/components/StatusLine';
import { Text } from '@astryxdesign/core/Text';
import { BellIcon } from '@phosphor-icons/react';
import { errorMessage } from '@/lib/errors';
import { pushState, subscribeCustomer, subscribeOwner } from './push';

/** Push opt-in with an honest status for every platform; calendar stays the fallback. */
export function NotificationOptIn({ audience, slug, token }: { audience: 'customer' | 'owner'; slug: string; token?: string }) {
  const qc = useQueryClient();
  const state = useQuery({ queryKey: ['push-state', slug, audience], queryFn: () => pushState(slug), staleTime: 0 });
  const sub = useMutation({
    mutationFn: () => (audience === 'owner' ? subscribeOwner(slug) : subscribeCustomer(slug, token!)),
    onSettled: () => qc.invalidateQueries({ queryKey: ['push-state', slug, audience] }),
  });

  if (state.isPending) return null;
  const s = state.data;
  if (!s || s.kind === 'unconfigured') {
    return <Text color="secondary" size="sm">Push-уведомления у этой студии не настроены. {audience === 'customer' ? 'Добавьте запись в календарь — он напомнит о визите.' : ''}</Text>;
  }
  if (s.kind === 'unsupported') {
    return (
      <Text color="secondary" size="sm">
        {s.reason === 'ios-not-installed'
          ? 'На iPhone уведомления работают только в установленном приложении: «Поделиться» → «На экран Домой», затем откройте его с главного экрана.'
          : s.reason === 'no-worker'
            ? 'Уведомления доступны в установленной версии приложения.'
            : 'Этот браузер не поддерживает push-уведомления.'}
        {audience === 'customer' ? ' Пока используйте календарь.' : ''}
      </Text>
    );
  }
  if (s.kind === 'denied') return <Text color="secondary" size="sm">Уведомления запрещены в настройках браузера для этого сайта.</Text>;
  if (s.kind === 'subscribed') return <StatusLine variant="success" label={audience === 'owner' ? 'Уведомления о записях включены на этом устройстве' : 'Напоминание придёт на это устройство'} />;
  return (
    <>
      <Button label={audience === 'owner' ? 'Включить уведомления о записях' : 'Напомнить о визите'} variant="secondary" width="100%" icon={<BellIcon size={20} />} isLoading={sub.isPending} onClick={() => sub.mutate()} />
      {sub.isError ? <Banner status="error" title="Не удалось включить уведомления" description={sub.error.message === 'permission_denied' ? 'Разрешение не выдано.' : errorMessage(sub.error)} /> : null}
    </>
  );
}
